import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmtError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Send, ShieldAlert, Phone, Navigation, ShieldCheck, Users } from "lucide-react";

const SUGGESTIONS = [
  "I feel unsafe walking home",
  "First aid for heavy bleeding",
  "Tips for a late-night cab ride",
  "How does Guardian Journey work?",
];

const TONE = {
  danger: "bg-[#DC2626] text-white",
  dark: "bg-[#111827] text-white",
  teal: "bg-[#0E7C66] text-white",
  purple: "bg-[#C4B5FD]/40 text-[#4c1d95]",
};

const ActionIcon = ({ type }) => {
  if (type === "call") return <Phone className="w-4 h-4" />;
  if (type === "journey_sos") return <ShieldAlert className="w-4 h-4" />;
  return <Navigation className="w-4 h-4" />;
};

export default function GuardianAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]); // {role, content, actions?, emergency?, fallback?}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(localStorage.getItem("ll_ai_session"));

  if (!sessionRef.current) {
    sessionRef.current = `guardian-${user?.id || "anon"}-${Date.now()}`;
    localStorage.setItem("ll_ai_session", sessionRef.current);
  }

  useEffect(() => {
    api
      .get(`/assistant/history?session_id=${sessionRef.current}`)
      .then((r) => setMessages((r.data.messages || []).map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const runAction = async (a) => {
    if (a.type === "call") {
      window.location.href = a.value;
    } else if (a.type === "navigate") {
      navigate(a.value);
    } else if (a.type === "journey_sos") {
      try {
        await api.post(`/journeys/${a.journey_id}/sos`, { mode: a.mode, origin: window.location.origin });
        toast[a.mode === "silent" ? "success" : "error"](
          a.mode === "silent" ? "Silent SOS activated discreetly." : "SOS active — your circle is being alerted."
        );
        navigate("/journey");
      } catch (e) {
        toast.error(fmtError(e?.response?.data?.detail));
      }
    }
  };

  const send = useCallback(
    async (text) => {
      const msg = (text ?? input).trim();
      if (!msg || busy) return;
      setInput("");
      setBusy(true);
      setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", pending: true }]);
      try {
        const res = await fetch(`${API}/assistant/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ll_token")}` },
          body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
        });
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const part of parts) {
            const line = part.replace(/^data: /, "").trim();
            if (!line) continue;
            let ev;
            try {
              ev = JSON.parse(line);
            } catch {
              continue;
            }
            setMessages((m) => {
              const copy = m.slice();
              const idx = copy.length - 1;
              const last = { ...copy[idx] };
              if (ev.meta) {
                last.actions = ev.meta.actions;
                last.emergency = ev.meta.emergency;
              } else if (ev.delta) {
                last.content = (last.content || "") + ev.delta;
                last.pending = false;
                if (ev.fallback) last.fallback = true;
              } else if (ev.done) {
                last.pending = false;
              }
              copy[idx] = last;
              return copy;
            });
          }
        }
      } catch (e) {
        setMessages((m) => {
          const copy = m.slice();
          const idx = copy.length - 1;
          const last = { ...copy[idx] };
          last.content =
            "I couldn't reach the assistant. If this is urgent, use the SOS button or call 112. You can also start a Guardian Journey.";
          last.fallback = true;
          last.pending = false;
          last.actions = [
            { type: "call", label: "Call Emergency 112", value: "tel:112", tone: "danger" },
            { type: "navigate", label: "Get Help (SOS)", value: "/emergency/sos", tone: "danger" },
          ];
          copy[idx] = last;
          return copy;
        });
      } finally {
        setBusy(false);
      }
    },
    [input, busy]
  );

  return (
    <div className="flex flex-col min-h-[calc(100dvh-7rem)]" data-testid="assistant-page">
      {/* Header */}
      <div className="bg-[#0E7C66] text-white px-5 pt-8 pb-4 rounded-b-[1.5rem] relative overflow-hidden shrink-0">
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black font-head leading-tight">Guardian AI</h1>
            <p className="text-white/85 text-xs">Your calm safety companion · always here</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="assistant-scroll">
        {messages.length === 0 && (
          <div className="text-center pt-6">
            <div className="w-14 h-14 rounded-2xl bg-[#0E7C66]/10 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-7 h-7 text-[#0E7C66]" />
            </div>
            <p className="font-bold text-[#111827]">How can I help keep you safe?</p>
            <p className="text-sm text-[#111827]/50 mt-1 mb-4">Ask about safety, first aid, or your journey. In an emergency I'll show instant actions.</p>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`assistant-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="text-left text-sm font-semibold bg-white border border-[#111827]/10 rounded-2xl px-4 py-3 active:scale-[0.99] transition-transform"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} data-testid="assistant-message" className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={m.role === "user" ? "max-w-[82%]" : "w-full"}>
              {m.emergency && (
                <div data-testid="assistant-emergency-banner" className="flex items-center gap-2 text-[#DC2626] font-bold text-sm mb-2">
                  <ShieldAlert className="w-4 h-4" /> Emergency — act now
                </div>
              )}
              <div
                className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#0E7C66] text-white rounded-br-md"
                    : "bg-white border border-[#111827]/8 text-[#111827] rounded-bl-md"
                }`}
              >
                {m.content || (m.pending ? <span className="text-[#111827]/40">Guardian is thinking…</span> : "")}
                {m.fallback && <span className="block text-[10px] text-[#111827]/40 mt-1">Offline safety guidance</span>}
              </div>
              {m.actions && m.actions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {m.actions.map((a, j) => (
                    <button
                      key={j}
                      data-testid={`assistant-action-${a.type}`}
                      onClick={() => runAction(a)}
                      className={`flex items-center gap-1.5 text-sm font-bold rounded-full px-3.5 py-2 active:scale-95 transition-transform ${TONE[a.tone] || TONE.teal}`}
                    >
                      <ActionIcon type={a.type} /> {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-[#111827]/8 bg-white px-3 py-2.5">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Ask Guardian AI…"
            className="flex-1 resize-none rounded-2xl border border-[#111827]/12 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7C66]/40 max-h-28"
          />
          <button
            data-testid="assistant-send"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="w-11 h-11 rounded-2xl bg-[#0E7C66] text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform shrink-0"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[10px] text-[#111827]/40 text-center mt-1.5 flex items-center justify-center gap-1">
          <Users className="w-3 h-3" /> Guidance only · not a substitute for emergency services (112)
        </p>
      </div>
    </div>
  );
}
